import type { AtRosterData } from './types';

/**
 * AI Team roster mock — nine agents in three groups (specs 36 + 37). The
 * AI Receptionist, AI SMS and AI Email are three cards (the Receptionist's old
 * channel rows). Each card carries its agent page's own numbers (30 days;
 * the three answering agents show today). The money tile is the exact sum of
 * the money on the cards: $43,900 + $96,400 + $11,240 + $24,890 = $176,430.
 * Hold state and the practice's plan live in team-hold/teamHold (the store).
 */

/** The shared tail on the six non-answering agents' hold modals (spec 36 §5.2). */
export const AT_HOLD_TAIL =
  'Switch it back on any time — held messages go at their next window; anything past its date is dropped.';

/** Booked-or-collected tile = the money on the cards, added up (spec 36 §3). */
export const AT_MONEY_TILE = '$176,430';

export const AI_TEAM_ROSTER: AtRosterData = {
  practiceName: 'Alamo Heights Dental',
  title: 'Your AI team',
  teamStatus: '9 of 9 working',
  kpis: [],
  groups: [
    {
      key: 'answers',
      title: 'Answers patients',
      subtitle: 'Replies when a patient calls, texts or emails.',
      agents: [
        {
          key: 'receptionist', name: 'AI Receptionist', icon: 'phone', state: 'working', planTag: 'Add-on',
          oneLine: 'Answers every call the moment it arrives — booking, rescheduling and routing without staff.',
          line: 'Answers on its own · 14 of 14 actions · also places AI calls for Reminders, Recall, Gap Filler and A/R',
          channels: [{ label: 'Call', dir: 'both' }],
          stats: [
            { label: 'calls today', value: '24' },
            { label: 'booked', value: '21' },
            { label: 'warm transfers', value: '3' },
            { label: 'average call', value: '2m 47s' },
          ],
          answering: true,
          openTo: '/ai-team/receptionist', seenLabel: 'said', logKey: 'receptionist',
          holdCopy: 'ELVA stops answering calls. Calls ring your front desk. AI calls from Reminders, Recall, Gap Filler and A/R stop too and fall back to text.',
          holdN: '—',
          whatItAdds: 'Also turns on AI calls for Reminders, Recall, Gap Filler and A/R, warm transfer and the live call monitor.',
        },
        {
          key: 'sms', name: 'AI SMS', icon: 'chat', state: 'working', planTag: 'Included in Core',
          oneLine: 'Answers every text the moment it arrives — booking and routing without staff.',
          line: 'Answers on its own · 9 of 14 actions',
          channels: [{ label: 'Text', dir: 'both' }],
          stats: [
            { label: 'conversations today', value: '31' },
            { label: 'booked', value: '13' },
            { label: 'paused for staff', value: '2' },
          ],
          answering: true,
          openTo: '/messages', openLabel: 'Open Messages', seenLabel: 'sent', logKey: 'sms',
          holdCopy: 'ELVA stops answering texts. Texts wait in Messages for your team. Reminders, Recall and the other agents still send their own texts.',
          holdN: '—',
          whatItAdds: '',
        },
        {
          key: 'email', name: 'AI Email', icon: 'mail', state: 'working', planTag: 'Included in Core',
          oneLine: 'Drafts a reply to every email — you send.',
          line: 'Drafts, you send · 7 of 14 actions',
          channels: [{ label: 'Email', dir: 'both' }],
          stats: [
            { label: 'drafts today', value: '9' },
            { label: 'booked', value: '4' },
            { label: 'sent unedited', value: '94%' },
          ],
          answering: true,
          openTo: '/email', openLabel: 'Open Email', seenLabel: 'drafted', logKey: 'email',
          holdCopy: 'ELVA stops drafting. Emails wait in Email for your team, with no draft.',
          holdN: '—',
          whatItAdds: '',
        },
      ],
    },
    {
      key: 'outreach',
      title: 'Reaches out to patients',
      subtitle: 'Starts the conversation, from a list you define.',
      agents: [
        {
          key: 'gap-filler', name: 'Gap Filler', icon: 'calendar', state: 'working', planTag: 'Add-on',
          oneLine: "Fills tomorrow's open chair time from your short-call list.",
          channels: [{ label: 'Text', dir: 'both' }, { label: 'Email', dir: 'both' }, { label: 'Call', dir: 'both' }],
          stats: [
            { label: 'open now', value: '3' },
            { label: 'filled', value: '74' },
            { label: 'booked', value: '$43,900', strong: true },
          ],
          openTo: '/ai-team/gap-filler', seenLabel: 'sent', logKey: 'gap-filler', needsReceptionist: true,
          holdCopy: 'Open gaps stop getting offers. Offers already sent stay open until they expire.',
          holdN: '—',
          whatItAdds: 'Finds open chair time and offers it to the right patients, a few at a time — the first yes books it.',
        },
        {
          key: 'treatment', name: 'Treatment Coordinator', icon: 'clipboard', state: 'working', planTag: 'Add-on',
          oneLine: "Follows up on presented treatment until it's booked.",
          channels: [{ label: 'Text', dir: 'both' }, { label: 'Email', dir: 'both' }, { label: 'Call', dir: 'not_permitted' }],
          stats: [
            { label: 'messaged', value: '288' },
            { label: 'booked', value: '47' },
            { label: 'treatment booked', value: '$96,400', strong: true },
          ],
          openTo: '/ai-team/treatment', seenLabel: 'sent', logKey: 'treatment',
          holdCopy: 'It stops starting conversations: {n} messages due today won’t go. Replies are still answered, and patients already in Calls stay there.',
          holdN: '12',
          whatItAdds: 'Finds diagnosed treatment that isn’t booked and follows up with a private plan page for each patient.',
        },
        {
          key: 'invisalign', name: 'Invisalign Coordinator', icon: 'sparkle', state: 'working', planTag: 'Add-on',
          tag: 'Invis-Line (210) 555-0199',
          oneLine: 'Runs the Invis-Line and nurtures aligner leads to a consult.',
          channels: [{ label: 'Email', dir: 'both' }, { label: 'Text', dir: 'both' }, { label: 'Call', dir: 'in_only' }],
          stats: [
            { label: 'prospects', value: '63' },
            { label: 'ready', value: '7' },
            { label: 'consults booked', value: '5', strong: true },
          ],
          openTo: '/ai-team/invisalign', seenLabel: 'sent', logKey: 'invisalign', needsReceptionist: true,
          holdCopy: 'The programme stops sending: {n} messages due today won’t go. The Invis-Line still answers.',
          holdN: '2',
          whatItAdds: 'Finds patients interested in clear aligners and follows up with them, with their yes, until they book a consult.',
        },
        {
          key: 'ar', name: 'Accounts receivable', icon: 'credit', state: 'working', planTag: 'Add-on',
          oneLine: "Works overdue patient balances so staff don't have to.",
          channels: [{ label: 'Text', dir: 'both' }, { label: 'Email', dir: 'both' }, { label: 'Call', dir: 'both' }],
          stats: [
            { label: 'collected', value: '$11,240', strong: true },
            { label: 'paid', value: '74' },
            { label: 'in a follow-up', value: '82' },
          ],
          openTo: '/ai-team/ar', seenLabel: 'sent', logKey: 'ar', needsReceptionist: true,
          holdCopy: 'Balance follow-ups and AI calls stop: {n} messages due today won’t go. Payment links already sent keep working.',
          holdN: '14',
          whatItAdds: 'Follows up on patient balances with a payment link in every message.',
        },
        {
          key: 'recall', name: 'Recall', icon: 'refresh', state: 'working', planTag: 'Included in Core',
          oneLine: "Brings patients back when they're due for a visit.",
          channels: [{ label: 'Text', dir: 'both' }, { label: 'Email', dir: 'both' }, { label: 'Call', dir: 'both' }],
          stats: [
            { label: 'recalls running', value: '6' },
            { label: 'booked', value: '131' },
            { label: 'scheduled', value: '$24,890', strong: true },
          ],
          openTo: '/ai-team/recall', seenLabel: 'sent', logKey: 'recall', needsReceptionist: true,
          holdCopy: 'Running recalls stop sending: {n} messages due today won’t go. Patients keep their place and pick up where they left off.',
          holdN: '46',
          whatItAdds: '',
        },
      ],
    },
    {
      key: 'appointments',
      title: 'Keeps appointments',
      subtitle: 'Talks about a visit the patient already has.',
      agents: [
        {
          key: 'reminders', name: 'Appointment Reminders', icon: 'bell', state: 'working', planTag: 'Included in Core',
          oneLine: 'Confirms upcoming visits and sends post-op follow-ups.',
          channels: [{ label: 'Text', dir: 'both' }, { label: 'Email', dir: 'both' }, { label: 'Call', dir: 'both' }],
          stats: [
            { label: 'sent', value: '2,584' },
            { label: 'confirmed', value: '812' },
            { label: 'no-show rate', value: '4.1%', strong: true },
          ],
          openTo: '/ai-team/reminders', seenLabel: 'sent', logKey: 'reminders', needsReceptionist: true,
          holdCopy: 'It stops confirmations, pre-visit texts and review asks: {n} messages due today won’t go. Post-op checks stop too — your team will need to make them.',
          holdN: '38',
          whatItAdds: '',
        },
      ],
    },
  ],
  brainTitle: 'The Brain',
  brainSubtitle: 'shared by every agent',
  brainChips: [
    { label: 'Who goes first', icon: 'list', to: '/settings/ai/general#who-goes-first' },
    { label: 'How often', icon: 'clock', to: '/settings/ai/general#how-often' },
    { label: 'Personality and voice', icon: 'sparkle', to: '/settings/ai/general#personality' },
    { label: 'Money', icon: 'dollar', to: '/settings/ai/general#money' },
    { label: 'Languages', icon: 'translate', to: '/settings/ai/general#languages' },
    { label: 'Your rules', icon: 'book', to: '/settings/ai/train' },
    { label: 'Knowledge base', icon: 'book', to: '/settings/ai/knowledge-base' },
    { label: 'Quiet hours', icon: 'clock', to: '/settings/ai/channels/sms' },
    { label: 'What ELVA can do', icon: 'shield-check', to: '/settings/ai/channels/sms' },
  ],
};

/** Kept for the page's call site; the view now reads hold + plan from the store. */
export function aiTeamRoster(): AtRosterData {
  return AI_TEAM_ROSTER;
}
